import React, { useState, useEffect, useRef } from 'react';
import fotoPredio from '../../assets/fabrica-predio.jpg';
import fotoGabaritos from '../../assets/fabrica-gabaritos.jpg';
import { useAuth } from '../../context/AuthContext';

export default function Login() {
  const { login, API } = useAuth();

  const [form, setForm] = useState({ usuario: '', senha: '' });
  const [manterConectado, setManterConectado] = useState(false);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [capsLockAtivo, setCapsLockAtivo] = useState(false);
  const submitRef = useRef(null);

  /* ⚠ O USUÁRIO LEMBRADO SAIU (07/09/2026), e não foi só a caixinha.
     A caixa de seleção era o CONSENTIMENTO: sem ela não há como a
     pessoa autorizar, e lembrar sem autorização é expor para a próxima
     quem usou o aparelho antes — num sistema em que a conferência é
     assinada. Saíram junto a chave `sgq_login_lembrado` do
     `localStorage`, o estado e a linha do usuário na tela.

     Se voltar algum dia, tem de voltar com caixinha própria, desmarcada
     por padrão, guardando SÓ o login — nunca o horário do último
     acesso, que é dado de auditoria. */

  // Mantém o botão "Entrar" alcançável quando o teclado mobile abre (22/07/2026,
  // print real do Juan — o botão ficava atrás do teclado/barra de sugestão do
  // Chrome). O navegador só rola o campo focado pra dentro da tela, nunca o
  // botão logo abaixo dele — então precisamos empurrar esse scroll na mão.
  // `visualViewport` é a forma correta de detectar teclado aberto: diferente
  // de `resize`/`vh`, ele reflete a área REALMENTE visível (o layout viewport
  // não encolhe em vários navegadores mobile quando o teclado sobe).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return; // navegador sem suporte: sem quebra, só sem o auto-scroll
    const alturaCheia = vv.height;
    let timeoutId;
    const aoRedimensionar = () => {
      clearTimeout(timeoutId);
      // Pequeno debounce: o resize pode disparar várias vezes durante a
      // animação do teclado abrindo; só reagimos quando ele assenta.
      timeoutId = setTimeout(() => {
        const provavelTecladoAberto = vv.height < alturaCheia * 0.75;
        if (provavelTecladoAberto) {
          submitRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 120);
    };
    vv.addEventListener('resize', aoRedimensionar);
    return () => { vv.removeEventListener('resize', aoRedimensionar); clearTimeout(timeoutId); };
  }, []);

  // Se o login falhar com o teclado ainda aberto, a mensagem de erro empurra
  // o botão pra baixo (ocupa espaço entre o campo e o divisor) — rola de novo
  // pra garantir que ele continua visível sem a pessoa precisar mexer no dedo.
  useEffect(() => {
    if (erro) submitRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [erro]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErro('');
  };

  const checarCapsLock = (e) => {
    setCapsLockAtivo(e.getModifierState ? e.getModifierState('CapsLock') : false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.usuario || !form.senha) return setErro('Informe usuário e senha.');
    setCarregando(true);
    try {
      // Login não pergunta CNPJ (08/07/2026) — cada registro carrega
      // sua própria empresa (setor, colaborador, etc.), nunca a sessão.
      await login(form.usuario, form.senha, manterConectado);
      // Recarrega a página inteira em vez de navegar via React Router.
      // Motivo: a navegação "suave" do router mantinha o app montado e o
      // Dashboard às vezes renderizava antes do mapa de permissões propagar
      // — a tela ficava presa no "Carregando" e só um F5 manual resolvia
      // (bug recorrente do login). Recarregar reproduz exatamente o F5 que
      // sempre funcionou: a página reinicia, lê o token/usuário já salvos no
      // localStorage e monta o Dashboard do zero, com os acessos prontos.
      //
      // Achado real ao testar num navegador de verdade (09/08/2026): como
      // essa recarga sempre apontava direto pra /dashboard, nunca passava
      // por /login ou /portal de novo — o RotaPublica (decide se mostra o
      // Seletor de Portal) nunca era alcançado nesse fluxo, e Master RH/
      // Geral caía sempre no SGQ, sem chance de ver o seletor. Checando
      // aqui, antes da recarga, decide o destino certo sem perder o
      // comportamento de recarga completa que já resolvia o outro bug.
      //
      // Segundo achado real, mais sério (mesma data, pergunta do Juan):
      // "SGQ" não pode ser assumido como acesso garantido — Master RH/
      // Geral não têm nenhum vínculo real lá (confirmado com HTTP real:
      // master:false, todo nível 'nenhum'). Checa os 3 portais agora, e
      // manda direto pro único de verdade quando só resta 1 — sem passar
      // por /portal só pra ele auto-redirecionar de novo no passo seguinte.
      let destino = '/dashboard';
      try {
        const [sgq, rh, geral] = await Promise.allSettled([
          API.get('/acessos/tenho-acesso', { timeout: 3000 }),
          API.get('/rh/acessos/tenho-acesso', { timeout: 3000 }),
          API.get('/administracao-geral/tenho-acesso', { timeout: 3000 }),
        ]);
        const temSGQ = sgq.status === 'fulfilled' && !!sgq.value.data?.tem_acesso;
        const temRH = rh.status === 'fulfilled' && !!rh.value.data?.tem_acesso;
        const temGeral = geral.status === 'fulfilled' && !!geral.value.data?.tem_acesso;
        const total = (temSGQ?1:0) + (temRH?1:0) + (temGeral?1:0);
        if (total >= 2) destino = '/portal';
        else if (temRH) destino = '/rh/dashboard';
        else if (temGeral) destino = '/administracao-geral';
        // else fica '/dashboard' — temSGQ true, ou nenhum dos 3 (conta
        // sem vínculo nenhum, caso extremo) — SGQ é o destino seguro.
      } catch (e) {
        /* Na dúvida, cai no SGQ — comportamento de sempre. O fallback é
           intencional: falhar a descoberta de portal não pode impedir
           o login. O registro existe para a falha não virar mistério. */
        console.debug('[Login/destino]', e.message);
      }
      window.location.assign(destino);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao conectar. Verifique se o sistema está rodando.');
      setCarregando(false);
    }
    // sem finally: em caso de sucesso a página recarrega, não precisa resetar estado
  };

  /* ⚠ A ENTRADA É SEMPRE ESCURA (07/09/2026, decisão do dono do produto).
     O botão de tema saiu: não havia o que ele resolvesse ali. Ninguém
     escolhe tema antes de entrar, e depois de entrar vale a preferência
     da conta — como sempre valeu.

     A classe no `body` faz duas coisas de uma vez. Ela pinta o fundo do
     DOCUMENTO de preto, e é isso que fecha a faixa vazia embaixo da
     tela: `100vh` é medido antes do `zoom` da raiz, então a cena
     renderiza a 94% da altura no computador e sobravam 65px brancos
     numa tela de 1080. Com o documento preto, sobrando ou faltando, o
     que aparece atrás é a mesma cor.

     ⚠ E ELA PRECISA SAIR NA DESMONTAGEM. Entrar recarrega a página
     inteira (ver o `window.location.assign` acima), então na prática a
     limpeza raramente roda — mas ela cobre quem sai do login sem
     entrar, e sem isso o resto do sistema herdaria fundo preto no tema
     claro. */
  useEffect(() => {
    document.body.classList.add('tela-entrada');
    const temaAnterior = document.documentElement.dataset.tema;
    document.documentElement.dataset.tema = 'dark';
    return () => {
      document.body.classList.remove('tela-entrada');
      if (temaAnterior) document.documentElement.dataset.tema = temaAnterior;
    };
  }, []);

  return (
    /* ⚠ PAINEL DE UM LADO, A FÁBRICA FECHANDO O RESTO (07/09/2026).
       A versão anterior era uma coluna centrada num fundo preto vazio —
       e num monitor de 1440 sobrava metade da tela sem nada.

       ⚠ A FOTO É `import` DE ASSET, não rota. O login acontece ANTES de
       existir sessão: uma rota autenticada devolveria 401 e a imagem
       nunca carregaria. É o mesmo defeito que a capa da sala teve,
       evitado aqui de origem.

       ⚠ NO CELULAR ela vira FUNDO, não faixa. A letra "SOLLOS." do
       prédio está a 60% da largura e 50% da altura da foto; com `cover`
       numa moldura em pé não há sobra vertical, então quem a traz para
       o enquadramento é o eixo X em 64% — e quem a deixa VISÍVEL é o
       véu, que começa em 35% no topo. Enquadrar e velar são dois
       problemas diferentes. */
    <div className="en-cena">
      {/* ⚠ A PEÇA FLUTUA SOBRE A CENA — não há mais `.en-painel`
          (11/09/2026). A coluna sólida encostada na borda virou uma peça
          clara com canto arredondado e sombra, e a cena escura passou a
          ser o palco dela.

          ⚠ E A FOTO É COLUNA, NÃO FUNDO. Quando ela virou fundo de tela
          inteira, o `cover` reenquadrou os gabaritos sozinho e entraram
          a máquina e o telhado. Mesmo arquivo, moldura diferente — a
          nota do `.en-foto` no `index.css` tem a conta. */}
      <div className="en-cx en-surge">
        <span className="en-marca">SGQ.</span>

        {/* ⚠ O TRILHO NÃO CONTA PASSOS. A sequência tem quatro formas
            possíveis e, aqui, o total ainda não se conhece: o primeiro
            acesso e a quantidade de portas só aparecem depois que o
            servidor responde, alguns blocos abaixo neste mesmo arquivo.
            Marcar "1 de 3" seria prometer o que não se sabe. */}
        <div className="en-trilho" aria-hidden="true">
          <i className="w on" /><i className="n" /><i className="n" />
        </div>
        <div className="en-nome">Entrar.</div>

        <form onSubmit={handleSubmit} className="en-fm">
          <label className="en-cp">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8.25" r="4.2" />
              <path d="M 3.75 21 c 0 -4.5 3.75 -7.5 8.25 -7.5 s 8.25 3 8.25 7.5" />
            </svg>
            <span className="en-cp-txt">
              <label htmlFor="usuario">Usuário</label>
              <input id="usuario" name="usuario" type="text" placeholder="seu.usuario"
                value={form.usuario} onChange={handleChange} autoComplete="username" autoFocus
                spellCheck={false} enterKeyHint="next"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('senha')?.focus(); } }} />
            </span>
          </label>

          <label className="en-cp">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5.25" y="10.5" width="13.5" height="10.5" rx="2.25" />
              <path d="M 8.25 10.5 V 6.75 a 3.75 3.75 0 0 1 7.5 0 V 10.5" />
            </svg>
            <span className="en-cp-txt">
              <label htmlFor="senha">Senha</label>
              <input id="senha" name="senha" type={mostrarSenha ? 'text' : 'password'}
                placeholder="••••••••" value={form.senha} onChange={handleChange}
                autoComplete="current-password" enterKeyHint="go"
                onKeyDown={checarCapsLock} onKeyUp={checarCapsLock} />
            </span>
            <button type="button" className="en-olho" tabIndex={-1}
              onClick={() => setMostrarSenha(v => !v)} aria-label={mostrarSenha ? 'Ocultar a senha' : 'Mostrar a senha'}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 3 12 c 2.25 -4.5 6 -6.75 9 -6.75 s 6.75 2.25 9 6.75 c -2.25 4.5 -6 6.75 -9 6.75 S 5.25 16.5 3 12 z" />
                <circle cx="12" cy="12" r="3" />
                {mostrarSenha && <line x1="3" y1="3" x2="21" y2="21" />}
              </svg>
            </button>
          </label>

          {capsLockAtivo && (
            <div className="en-recado">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 12 3 l 7.5 7.5 h -4.5 v 7.5 H 9 V 10.5 H 4.5 l 7.5 -7.5 z" />
                <line x1="6" y1="21" x2="18" y2="21" />
              </svg>
              Caps Lock está ativado
            </div>
          )}

          {erro && <div className="en-recado erro">{erro}</div>}

          <button ref={submitRef} type="submit" className="en-bt" disabled={carregando}>
            {carregando ? (<><span className="en-spinner" />Entrando...</>) : 'Entrar'}
          </button>

          {/* ⚠ DEPOIS do botão, e de propósito. O botão é o que TEM de
              caber acima do teclado; esta opção só importa antes de
              apertá-lo, quando o teclado ainda não está aberto. */}
          <label className="en-manter">
            <input type="checkbox" checked={manterConectado}
              onChange={e => setManterConectado(e.target.checked)} />
            <span className="en-cx-box" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 4.5 12.75 l 4.8 4.8 L 19.5 6.75" />
              </svg>
            </span>
            Manter conectado
          </label>
          {manterConectado && (
            <span className="en-manter-aviso">
              Fica <b>30 dias</b> sem pedir senha. Não marque em aparelho compartilhado —
              sem ela a sessão dura 8 horas.
            </span>
          )}
        </form>

        {/* ⚠ A ASSINATURA MORA DENTRO DA PEÇA (11/09/2026). Ela chegou a
            ser uma pílula preta flutuando sobre a foto — e aí era a
            única peça preta da tela em que não havia nada para clicar,
            igual a um alvo sem ser um. Aqui ela é o que sempre foi. */}
        <div className="en-pe">
          <span>SGQ. SOLLOS.</span>
          <span className="en-ano">Uso interno {new Date().getFullYear()}</span>
        </div>
      </div>

      <div className="en-foto" style={{
        '--foto-pc': `url(${fotoGabaritos})`,
        '--foto-cel': `url(${fotoPredio})`
      }}>
        <div className="en-slogan">
          Cada processo registrado.<br />
          Cada pedido rastreado.<br />
          Cada decisão documentada.
        </div>
      </div>
    </div>
  );
}
